import React, { useState, useEffect, useRef, memo, useMemo } from 'react';
import LineChart from '../charts/LineChart02';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';
import BeatLoader from 'react-spinners/BeatLoader';
import { tailwindConfig } from '../utils/Utils';
import { Chart } from 'chart.js';

// Add caching constants
const CHART_CACHE_KEY = 'dashboard_chart_cache_v2'; // v2 for consistency
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Helper functions for caching
const getCachedData = (key) => {
  try {
    const cached = localStorage.getItem(key);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Date.now() - parsed.timestamp < CACHE_DURATION) {
        return parsed.data;
      }
    }
  } catch (error) {
    console.error('Error reading cache:', error);
  }
  return null;
};

const setCachedData = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
  } catch (error) {
    console.error('Error setting cache:', error);
  }
};

// Memoized version of the LineChart component
const MemoizedLineChart = memo(LineChart);

function DashboardChart({ campaignIds }) {
  const [rawChartData, setRawChartData] = useState(null);
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [],
  });
  const [totals, setTotals] = useState({ plays: 0, ctas: 0, shares: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [chartView, setChartView] = useState('today');
  const [visibleDatasets, setVisibleDatasets] = useState({
    'Plays': true,
    'CTA Clicks': true,
    'Shares': true
  });

  // Add ref to store chart instance
  const chartRef = useRef(null);
  const isMountedRef = useRef(true);

  const chartOptions = useMemo(() => ({
    scales: {
      x: {
        type: 'category', // Use category for both views to avoid time scale issues
        ticks: {
          autoSkip: false,
          maxRotation: chartView === 'today' ? 45 : 0,
          color: tailwindConfig().theme.colors.gray[500],
          callback: chartView === '7days' ? function(value, index) {
            // For 7-day view, format the date labels nicely
            const labelValue = this.getLabelForValue(value);
            const date = new Date(labelValue);
            if (isNaN(date.getTime())) {
              return labelValue; // Return as-is if invalid date
            }
            return date.toLocaleDateString('en-US', { 
              month: 'short', 
              day: 'numeric' 
            });
          } : function(value, index) {
            // For today view, return the time block labels as-is
            return this.getLabelForValue(value);
          }
        },
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        min: 0,
        suggestedMin: 0,
        suggestedMax: 10,
        ticks: {
          maxTicksLimit: 5,
          color: tailwindConfig().theme.colors.gray[500],
          callback: (value) => Math.round(value),
        },
        grid: {
          color: tailwindConfig().theme.colors.gray[200],
        },
        afterDataLimits: (scale) => {
          if (scale.max < 10) {
            scale.max = 10;
          }
        },
      },
    },
    plugins: {
      legend: {
        display: false, // Disable default legend since we have custom buttons
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          title: function(context) {
            if (chartView === '7days') {
              // Format tooltip title for dates
              const dateStr = context[0].label;
              const date = new Date(dateStr);
              if (isNaN(date.getTime())) {
                return dateStr; // Return as-is if invalid date
              }
              return date.toLocaleDateString('en-US', { 
                weekday: 'long',
                month: 'short', 
                day: 'numeric' 
              });
            }
            // For today view, show the time block
            return context[0].label;
          },
          label: (context) => {
            const label = context.dataset.label || '';
            return `${label}: ${context.parsed.y}`;
          },
        },
      },
    },
    interaction: {
      mode: 'index',
      intersect: false,
    },
    elements: {
      line: {
        tension: 0.3,
        borderWidth: 2,
      },
      point: {
        radius: 0,
      },
    },
    maintainAspectRatio: false,
    resizeDelay: 200,
  }), [chartView]);

  // OPTIMIZED: Load raw data with sampling instead of full aggregation
  useEffect(() => {
    async function loadChartData() {
      setIsLoading(true);
      
      // Check cache first
      const cacheKey = `${CHART_CACHE_KEY}_${campaignIds?.join('_')}_${chartView}`;
      const cachedData = getCachedData(cacheKey);
      
      if (cachedData) {
        setRawChartData(cachedData.rawData);
        setTotals(cachedData.totals);
        setIsLoading(false);
        return;
      }

      try {
        let labels = [];
        let playCounts = [];
        let ctaCounts = [];
        let shareCounts = [];

        if (campaignIds && campaignIds.length > 0) {
          const campaignEventRef = collection(db, 'campaignEvent');
          
          if (chartView === '7days') {
            // OPTIMIZED: Sample-based 7-day view
            const normalizeDate = (date) => new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
            const today = normalizeDate(new Date());

            // Create date range array
            const dateRange = [];
            for (let i = 6; i >= 0; i--) {
              const date = new Date(today);
              date.setDate(date.getDate() - i);
              const dateStr = date.toISOString().split('T')[0];
              dateRange.push({ date, dateStr });
            }

            // Initialize results
            const statsMap = new Map(
              dateRange.map(item => [item.dateStr, { plays: 0, cta: 0, shares: 0 }])
            );

            // COST OPTIMIZATION: Sample recent entries and distribute across days
            for (const campaignId of campaignIds) {
              // Get recent entries (last 7 days sample)
              const weekAgo = new Date(today);
              weekAgo.setDate(weekAgo.getDate() - 7);
              
              const recentQuery = query(
                campaignEventRef,
                where('game_id', '==', campaignId),
                where('timestamp', '>=', weekAgo),
                orderBy('timestamp', 'desc'),
                limit(200) // Limit for cost control
              );
              
              const snapshot = await getDocs(recentQuery);
              const recentEntries = snapshot.docs.map(doc => doc.data());
              
              // Distribute across 7 days
              for (const { date, dateStr } of dateRange) {
                const dateStart = new Date(date);
                dateStart.setHours(0, 0, 0, 0);
                const dateEnd = new Date(date);
                dateEnd.setHours(23, 59, 59, 999);
                
                const dayEntries = recentEntries.filter(entry => {
                  const entryDate = entry.timestamp?.toDate() || new Date(entry.timestamp);
                  return entryDate >= dateStart && entryDate <= dateEnd;
                });
                
                const currentStats = statsMap.get(dateStr);
                currentStats.plays += dayEntries.length;
                currentStats.cta += dayEntries.filter(entry => entry.cta === true).length;
                currentStats.shares += dayEntries.filter(entry => entry.shared === true).length;
              }
            }

            // Convert map to arrays
            labels = dateRange.map(item => item.dateStr);
            playCounts = dateRange.map(item => statsMap.get(item.dateStr).plays);
            ctaCounts = dateRange.map(item => statsMap.get(item.dateStr).cta);
            shareCounts = dateRange.map(item => statsMap.get(item.dateStr).shares);
          } else {
            // OPTIMIZED: Sample-based today view
            const normalizeDate = (date) => new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
            const today = normalizeDate(new Date());
            const startOfDay = new Date(today);
            startOfDay.setHours(0, 0, 0, 0);

            // Group hours into 4-hour blocks to reduce complexity
            const timeBlocks = [
              { label: 'Early Morning', start: 0, end: 6 },
              { label: 'Morning', start: 6, end: 12 },
              { label: 'Afternoon', start: 12, end: 18 },
              { label: 'Evening', start: 18, end: 24 }
            ];

            labels = timeBlocks.map(block => block.label);
            playCounts = new Array(timeBlocks.length).fill(0);
            ctaCounts = new Array(timeBlocks.length).fill(0);
            shareCounts = new Array(timeBlocks.length).fill(0);

            // COST OPTIMIZATION: Sample recent entries instead of scanning by hour
            for (const campaignId of campaignIds) {
              // Get today's entries (limited sample)
              const todayQuery = query(
                campaignEventRef,
                where('game_id', '==', campaignId),
                where('timestamp', '>=', startOfDay),
                orderBy('timestamp', 'desc'),
                limit(100) // Limit to 100 entries per game for cost control
              );
              
              const snapshot = await getDocs(todayQuery);
              const todayEntries = snapshot.docs.map(doc => doc.data());
              
              // Distribute entries across time blocks
              for (let i = 0; i < timeBlocks.length; i++) {
                const blockStart = timeBlocks[i].start;
                const blockEnd = timeBlocks[i].end;
                
                const blockEntries = todayEntries.filter(entry => {
                  const entryDate = entry.timestamp?.toDate() || new Date(entry.timestamp);
                  const hour = entryDate.getHours();
                  return hour >= blockStart && hour < blockEnd;
                });
                
                playCounts[i] += blockEntries.length;
                ctaCounts[i] += blockEntries.filter(entry => entry.cta === true).length;
                shareCounts[i] += blockEntries.filter(entry => entry.shared === true).length;
              }
            }
          }
        } else {
          // Generate empty data
          if (chartView === '7days') {
            const normalizeDate = (date) => new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
            const today = normalizeDate(new Date());
            for (let i = 6; i >= 0; i--) {
              const date = new Date(today);
              date.setDate(today.getDate() - i);
              labels.push(date.toISOString().split('T')[0]);
              playCounts.push(0);
              ctaCounts.push(0);
              shareCounts.push(0);
            }
          } else {
            labels = ['Early Morning', 'Morning', 'Afternoon', 'Evening'];
            playCounts = new Array(4).fill(0);
            ctaCounts = new Array(4).fill(0);
            shareCounts = new Array(4).fill(0);
          }
        }

        const newChartData = {
          labels,
          datasets: [
            {
              label: 'Plays',
              data: playCounts,
              borderColor: '#9B59B6',
              backgroundColor: 'rgba(155, 89, 182, 0.2)',
              fill: true,
              borderWidth: 3,
              tension: 0.3,
              pointRadius: 0,
              pointHoverRadius: 5,
              pointBackgroundColor: '#9B59B6',
              pointHoverBackgroundColor: '#F1C40F',
              pointBorderWidth: 0,
              pointHoverBorderWidth: 2,
              pointHoverBorderColor: '#E67E22',
              clip: 20,
            },
            {
              label: 'CTA Clicks',
              data: ctaCounts,
              borderColor: '#3498DB',
              backgroundColor: 'rgba(52, 152, 219, 0.2)',
              fill: true,
              borderWidth: 3,
              tension: 0.3,
              pointRadius: 0,
              pointHoverRadius: 5,
              pointBackgroundColor: '#3498DB',
              pointHoverBackgroundColor: '#F1C40F',
              pointBorderWidth: 0,
              pointHoverBorderWidth: 2,
              pointHoverBorderColor: '#E67E22',
              clip: 20,
            },
            {
              label: 'Shares',
              data: shareCounts,
              borderColor: '#2ECC71',
              backgroundColor: 'rgba(46, 204, 113, 0.2)',
              fill: true,
              borderWidth: 3,
              tension: 0.3,
              pointRadius: 0,
              pointHoverRadius: 5,
              pointBackgroundColor: '#2ECC71',
              pointHoverBackgroundColor: '#F1C40F',
              pointBorderWidth: 0,
              pointHoverBorderWidth: 2,
              pointHoverBorderColor: '#E67E22',
              clip: 20,
            },
          ],
        };
        setRawChartData(newChartData);
        
        // Calculate totals from the new data
        const calculatedTotals = {
          plays: playCounts.reduce((sum, val) => sum + val, 0),
          ctas: ctaCounts.reduce((sum, val) => sum + val, 0),
          shares: shareCounts.reduce((sum, val) => sum + val, 0)
        };
        setTotals(calculatedTotals);
        
        // Cache the results
        setCachedData(cacheKey, {
          rawData: newChartData,
          totals: calculatedTotals
        });
        
      } catch (error) {
        console.error('Error loading chart data:', error);
        setChartData({
          labels: [],
          datasets: []
        });
        setTotals({ plays: 0, ctas: 0, shares: 0 });
      } finally {
        setIsLoading(false);
      }
    }

    loadChartData();
  }, [campaignIds, chartView]);

  // Update chart data when visibility changes OR when rawChartData changes
  useEffect(() => {
    if (rawChartData) {
      setChartData({
        ...rawChartData,
        datasets: rawChartData.datasets.map(dataset => ({
          ...dataset,
          hidden: !visibleDatasets[dataset.label]
        }))
      });
    }
  }, [visibleDatasets, rawChartData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // REMOVED: Real-time cache invalidation - using time-based expiration instead
  // This was expensive and unnecessary since we use 5-minute cache TTL

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-56" style={{fontFamily: '"Share Tech Mono", monospace'}}>
        <div className="text-center">
          <div className="text-4xl mb-3">📊</div>
          <BeatLoader size={15} color="#F1C40F" />
          <p className="text-sm mt-3 font-bold" style={{color: '#ECF0F1'}}>
            LOADING ANALYTICS...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" style={{fontFamily: '"Share Tech Mono", monospace'}}>
      <header className="px-5 py-4 border-b flex justify-between items-center" style={{
        borderColor: 'rgba(241, 196, 15, 0.3)'
      }}>
        <h2 className="font-black text-lg tracking-wider flex items-center gap-2" style={{
          color: '#F1C40F',
          textShadow: '1px 1px 0px #E67E22'
        }}>
          📊 {chartView === 'today' ? "TODAY'S ANALYTICS" : 'ANALYTICS HISTORY'}
        </h2>
        
        {/* View Toggle */}
        <div className="flex rounded-lg p-1" style={{
          background: 'rgba(52, 73, 94, 0.6)',
          border: '1px solid rgba(241, 196, 15, 0.3)'
        }}>
          <button
            onClick={() => setChartView('today')}
            className={`px-3 py-1 text-xs font-bold rounded-md transition-all duration-100 ${
              chartView === 'today'
                ? 'text-white shadow-sm'
                : 'hover:bg-opacity-50'
            }`}
            style={chartView === 'today' ? {
              background: 'linear-gradient(135deg, #F1C40F, #F39C12)',
              color: '#2C3E50',
              textShadow: '1px 1px 0px #E67E22'
            } : {
              color: '#ECF0F1'
            }}
          >
            TODAY
          </button>
          <button
            onClick={() => setChartView('7days')}
            className={`px-3 py-1 text-xs font-bold rounded-md transition-all duration-100 ${
              chartView === '7days'
                ? 'text-white shadow-sm'
                : 'hover:bg-opacity-50'
            }`}
            style={chartView === '7days' ? {
              background: 'linear-gradient(135deg, #F1C40F, #F39C12)',
              color: '#2C3E50',
              textShadow: '1px 1px 0px #E67E22'
            } : {
              color: '#ECF0F1'
            }}
          >
            7 DAYS
          </button>
        </div>
      </header>

      {/* Combined Legend Buttons with Stats */}
      <div className="px-5 pt-4 pb-2 flex gap-2 flex-wrap">
        <button
          onClick={() => setVisibleDatasets(prev => ({ ...prev, 'Plays': !prev['Plays'] }))}
          className={`inline-flex items-center px-3 py-2 rounded-lg text-xs font-bold transition-all duration-100 ${
            visibleDatasets['Plays'] ? 'shadow-sm' : ''
          }`}
          style={visibleDatasets['Plays'] ? {
            background: 'rgba(155, 89, 182, 0.2)',
            border: '1px solid #9B59B6',
            color: '#F1C40F'
          } : {
            background: 'rgba(52, 73, 94, 0.4)',
            border: '1px solid rgba(241, 196, 15, 0.2)',
            color: '#95A5A6'
          }}
        >
          <span className="w-2 h-2 rounded-full mr-2" style={{background: '#9B59B6'}}></span>
          <span className="font-black">{totals.plays.toLocaleString()}</span>
          <span className="ml-1.5">🎮 PLAYS</span>
        </button>

        <button
          onClick={() => setVisibleDatasets(prev => ({ ...prev, 'CTA Clicks': !prev['CTA Clicks'] }))}
          className={`inline-flex items-center px-3 py-2 rounded-lg text-xs font-bold transition-all duration-100 ${
            visibleDatasets['CTA Clicks'] ? 'shadow-sm' : ''
          }`}
          style={visibleDatasets['CTA Clicks'] ? {
            background: 'rgba(52, 152, 219, 0.2)',
            border: '1px solid #3498DB',
            color: '#F1C40F'
          } : {
            background: 'rgba(52, 73, 94, 0.4)',
            border: '1px solid rgba(241, 196, 15, 0.2)',
            color: '#95A5A6'
          }}
        >
          <span className="w-2 h-2 rounded-full mr-2" style={{background: '#3498DB'}}></span>
          <span className="font-black">{totals.ctas.toLocaleString()}</span>
          <span className="ml-1.5">🎯 CTA</span>
        </button>

        <button
          onClick={() => setVisibleDatasets(prev => ({ ...prev, 'Shares': !prev['Shares'] }))}
          className={`inline-flex items-center px-3 py-2 rounded-lg text-xs font-bold transition-all duration-100 ${
            visibleDatasets['Shares'] ? 'shadow-sm' : ''
          }`}
          style={visibleDatasets['Shares'] ? {
            background: 'rgba(46, 204, 113, 0.2)',
            border: '1px solid #2ECC71',
            color: '#F1C40F'
          } : {
            background: 'rgba(52, 73, 94, 0.4)',
            border: '1px solid rgba(241, 196, 15, 0.2)',
            color: '#95A5A6'
          }}
        >
          <span className="w-2 h-2 rounded-full mr-2" style={{background: '#2ECC71'}}></span>
          <span className="font-black">{totals.shares.toLocaleString()}</span>
          <span className="ml-1.5">🚀 SHARES</span>
        </button>
      </div>

      {/* Only render chart if we have valid data */}
      {chartData && chartData.labels && chartData.labels.length > 0 && chartData.datasets && chartData.datasets.length > 0 ? (
        <MemoizedLineChart data={chartData} options={chartOptions} width={595} height={248} />
      ) : (
        <div className="flex justify-center items-center h-56">
          <div className="text-center">
            <div className="text-4xl mb-3">📊</div>
            <p className="text-sm font-bold" style={{color: '#ECF0F1'}}>
              No data available
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default memo(DashboardChart);